import { useState } from 'react';
import {
  createCourseModule,
  deleteAssignment,
  deleteCourseModule,
  deleteExam,
  deleteLesson,
  reorderCourseClasswork,
  updateAssignment,
  updateCourseModule,
  updateExam,
  updateLesson,
} from '@/lib/api';
import { CLASSWORK_KINDS, buildClasswork, dueState, kindLabel } from '@/lib/courseRoom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  ArrowDown, ArrowUp, BookOpen, CheckCircle, ChevronDown, Circle, ClipboardList,
  Eye, EyeOff, FileText, Layers, Paperclip, Pencil, Plus, Trash2,
} from 'lucide-react';

const KIND_ICON = {
  material: { Icon: BookOpen, tone: 'text-emerald-600 bg-emerald-50' },
  assignment: { Icon: FileText, tone: 'text-blue-600 bg-blue-50' },
  quiz: { Icon: ClipboardList, tone: 'text-purple-600 bg-purple-50' },
};

const TONE_CLASS = {
  overdue: 'text-rose-600',
  soon: 'text-amber-600',
  upcoming: 'text-[#062B1A]/40',
  done: 'text-[#062B1A]/40',
  none: 'text-[#062B1A]/40',
};

const DELETERS = {
  material: deleteLesson,
  assignment: deleteAssignment,
  quiz: deleteExam,
};

const UPDATERS = {
  material: updateLesson,
  assignment: updateAssignment,
  quiz: updateExam,
};

/**
 * The classwork stream — everything this course teaches, in one list.
 *
 * Materials, assignments and quizzes sit together under their module, in the
 * order the class works through them, because that is how a course is actually
 * taught. Students read the list; the instructor gets the same list with a
 * Create button and per-row controls, including the move controls that set the
 * teaching sequence.
 *
 * The API and the database call a module a "topic"; only the words a person
 * reads were changed, so no migration was needed to speak one vocabulary.
 */
export default function ClassworkPane({
  courseId,
  lessons,
  assignments,
  exams,
  modules,
  selectedId,
  completedLessons,
  isEditor,
  onSelect,
  onCreate,
  onEdit,
  onChanged,
}) {
  const [topicDialog, setTopicDialog] = useState(null);
  const [topicForm, setTopicForm] = useState({ title: '', summary: '' });
  const [saving, setSaving] = useState(false);
  const [moving, setMoving] = useState(false);

  const groups = buildClasswork({ lessons, assignments, exams, modules });
  const total = (lessons?.length || 0) + (assignments?.length || 0) + (exams?.length || 0);

  /**
   * Move one item up or down within its module.
   *
   * The whole module is renumbered from 0 on every move rather than swapping
   * two `order` values, because items created before ordering existed all sit
   * at 0 — swapping those would change nothing. Materials, assignments and
   * quizzes go in the same request, so a quiz can move past a lesson.
   */
  const moveItem = async (group, index, direction) => {
    const target = index + direction;
    const items = [...group.items];
    if (target < 0 || target >= items.length) return;
    [items[index], items[target]] = [items[target], items[index]];
    try {
      setMoving(true);
      await reorderCourseClasswork(courseId, items.map((item, position) => ({
        item_id: item.id,
        kind: item.kind,
        module_id: group.isTopic ? group.id : '',
        order: position,
      })));
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reorder that');
    } finally {
      setMoving(false);
    }
  };

  const saveTopic = async () => {
    if (!topicForm.title.trim()) {
      toast.error('Give the module a title');
      return;
    }
    try {
      setSaving(true);
      if (topicDialog.mode === 'create') {
        await createCourseModule(courseId, { ...topicForm, order: (modules || []).length });
        toast.success('Module added');
      } else {
        await updateCourseModule(courseId, topicDialog.topic.id, topicForm);
        toast.success('Module updated');
      }
      setTopicDialog(null);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save this module');
    } finally {
      setSaving(false);
    }
  };

  const removeTopic = async (topicId) => {
    try {
      await deleteCourseModule(courseId, topicId);
      toast.success('Module removed — its classwork was kept');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove this module');
    }
  };

  const removeItem = async (item) => {
    try {
      await DELETERS[item.kind](item.id);
      toast.success(`${kindLabel(item.kind)} deleted`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete that');
    }
  };

  const toggleVisibility = async (item) => {
    const next = item.status === 'published' ? 'draft' : 'published';
    try {
      await UPDATERS[item.kind](item.id, { status: next });
      toast.success(next === 'published' ? 'Visible to students' : 'Hidden from students');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update visibility');
    }
  };

  return (
    <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm lg:col-span-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          Classwork
        </CardTitle>
        <p className="text-xs text-[#062B1A]/40">
          {total} item{total === 1 ? '' : 's'}
          {(modules || []).length ? ` • ${modules.length} module${modules.length === 1 ? '' : 's'}` : ''}
        </p>

        {isEditor && (
          <div className="flex gap-2 pt-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-8 bg-[#0F9D58] px-3 text-xs text-white hover:bg-[#0B7C45]" data-testid="classwork-create">
                  <Plus className="mr-1 size-3.5" />Create
                  <ChevronDown className="ml-1 size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {CLASSWORK_KINDS.map((kind) => {
                  const { Icon } = KIND_ICON[kind.id];
                  return (
                    <DropdownMenuItem key={kind.id} onClick={() => onCreate(kind.id)}>
                      <Icon className="mr-2 size-4" />{kind.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-3 text-xs"
              onClick={() => { setTopicDialog({ mode: 'create' }); setTopicForm({ title: '', summary: '' }); }}
            >
              <Layers className="mr-1 size-3.5" />Modules
            </Button>
          </div>
        )}
      </CardHeader>

      <ScrollArea className="h-[520px]">
        <CardContent className="space-y-4 pt-0">
          {groups.length === 0 && (
            <p className="py-8 text-center text-sm text-[#062B1A]/45">
              {isEditor
                ? 'Nothing here yet. Use Create to add your first material, assignment or quiz.'
                : 'Your instructor has not published any classwork yet.'}
            </p>
          )}

          {groups.map((group) => (
            <div key={group.id || 'ungrouped'}>
              <div className="mb-1.5 flex items-center gap-1 px-1">
                <p className="flex-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/55">
                  {group.title}
                </p>
                {isEditor && group.isTopic && (
                  <>
                    <button
                      type="button"
                      className="text-[#062B1A]/35 hover:text-[#062B1A]"
                      aria-label={`Edit ${group.title}`}
                      onClick={() => { setTopicDialog({ mode: 'edit', topic: group }); setTopicForm({ title: group.title, summary: group.summary }); }}
                    >
                      <Pencil className="size-3" />
                    </button>
                    <button
                      type="button"
                      className="text-rose-500/70 hover:text-rose-600"
                      aria-label={`Remove ${group.title}`}
                      onClick={() => removeTopic(group.id)}
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </>
                )}
              </div>
              {group.summary && <p className="mb-1 px-1 text-[11px] text-[#062B1A]/40">{group.summary}</p>}

              <div className="space-y-1">
                {group.items.length === 0 && (
                  <p className="px-1 py-2 text-xs text-[#062B1A]/30">Nothing under this module yet.</p>
                )}
                {group.items.map((item, idx) => {
                  const { Icon, tone } = KIND_ICON[item.kind];
                  const isSelected = selectedId === item.id;
                  const hidden = item.status !== 'published';
                  const completed = item.kind === 'material' && completedLessons.includes(item.id);
                  const due = item.kind !== 'material' ? dueState(item.due_date) : null;
                  return (
                    <div
                      key={item.id}
                      className={`rounded-lg transition-colors ${
                        isSelected ? 'border border-[#D4AF37]/30 bg-[#D4AF37]/10' : 'hover:bg-[#F7F8FA]'
                      }`}
                    >
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 p-3 text-left"
                        onClick={() => onSelect(item)}
                        data-testid={`classwork-item-${idx}`}
                      >
                        <span className={`flex size-8 flex-shrink-0 items-center justify-center rounded-lg ${tone}`}>
                          <Icon className="size-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={`block truncate text-sm ${isSelected ? 'font-medium text-[#062B1A]' : 'text-[#062B1A]/75'}`}>
                            {item.title}
                          </span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-[#062B1A]/40">
                            <span>{kindLabel(item.kind)}</span>
                            {due && <span className={TONE_CLASS[due.tone]}>{due.label}</span>}
                            {item.kind !== 'material' && <span>{item.total_marks} pts</span>}
                            {(item.materials || []).length > 0 && <Paperclip className="size-3" />}
                            {isEditor && hidden && <span className="font-medium text-amber-600">Draft</span>}
                          </span>
                        </span>
                        {!isEditor && item.kind === 'material' && (
                          <span className="flex-shrink-0">
                            {completed
                              ? <CheckCircle className="size-4 text-green-500" />
                              : <Circle className="size-4 text-[#062B1A]/20" />}
                          </span>
                        )}
                      </button>

                      {isEditor && (
                        <div className="flex items-center gap-0.5 border-t border-[#062B1A]/5 px-2 py-1">
                          {/* The teaching sequence, set on any item type — a
                              quiz moves past a lesson the same way a lesson
                              moves past a quiz. */}
                          <button
                            type="button"
                            className="rounded p-1 text-[#062B1A]/35 hover:bg-white hover:text-[#062B1A] disabled:opacity-25 disabled:hover:bg-transparent"
                            aria-label={`Move ${item.title} up`}
                            disabled={moving || idx === 0}
                            onClick={() => moveItem(group, idx, -1)}
                          >
                            <ArrowUp className="size-3.5" />
                          </button>
                          <button
                            type="button"
                            className="rounded p-1 text-[#062B1A]/35 hover:bg-white hover:text-[#062B1A] disabled:opacity-25 disabled:hover:bg-transparent"
                            aria-label={`Move ${item.title} down`}
                            disabled={moving || idx === group.items.length - 1}
                            onClick={() => moveItem(group, idx, 1)}
                          >
                            <ArrowDown className="size-3.5" />
                          </button>
                          <button
                            type="button"
                            className="rounded p-1 text-[#062B1A]/35 hover:bg-white hover:text-[#062B1A]"
                            aria-label={hidden ? `Publish ${item.title}` : `Hide ${item.title}`}
                            onClick={() => toggleVisibility(item)}
                          >
                            {hidden ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                          </button>
                          <button
                            type="button"
                            className="rounded p-1 text-[#062B1A]/35 hover:bg-white hover:text-[#062B1A]"
                            aria-label={`Edit ${item.title}`}
                            onClick={() => onEdit(item)}
                          >
                            <Pencil className="size-3.5" />
                          </button>
                          <button
                            type="button"
                            className="ml-auto rounded p-1 text-rose-500/70 hover:bg-rose-50 hover:text-rose-600"
                            aria-label={`Delete ${item.title}`}
                            onClick={() => removeItem(item)}
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </ScrollArea>

      <Dialog open={!!topicDialog} onOpenChange={(open) => !open && setTopicDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
              {topicDialog?.mode === 'create' ? 'Add a module' : 'Edit module'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Title *</label>
              <Input
                value={topicForm.title}
                onChange={(e) => setTopicForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Week 1 — Getting started"
                aria-label="Module title"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Summary</label>
              <Textarea
                value={topicForm.summary}
                onChange={(e) => setTopicForm((p) => ({ ...p, summary: e.target.value }))}
                aria-label="Module summary"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopicDialog(null)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" disabled={saving} onClick={saveTopic}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
